import axios, { AxiosResponse } from "axios";

import http from "../../http-common";

import {
	BenchmarkGoal,
	BenchmarkGoalFilter,
	BenchmarkGoalListResponseClientError,
	BenchmarkGoalListResponseOk,
	BenchmarkGoalListUnifiedResponse,
	BenchmarkGoalResult,
	CreateBenchmarkGoalRequest,
	DeleteBenchmarkGoalRequest,
	UpdateBenchmarkGoalRequest,
} from "../../types/Quality/Benchmark";

const DEFAULT_PAGE_SIZE = 100;

const buildQueryString = (params: Record<string, string | number | boolean | undefined | null>) => {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") {
			return;
		}
		searchParams.append(key, String(value));
	});

	return searchParams.toString();
};

export const listBenchmarkGoals = async (
	filter: BenchmarkGoalFilter = {}
): Promise<BenchmarkGoalListUnifiedResponse> => {
	const goals: BenchmarkGoal[] = [];
	let page: number | null = filter.page ?? 1;
	const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;
	const sharedFilter: BenchmarkGoalFilter = {
		...filter,
	};
	delete sharedFilter.page;
	delete sharedFilter.pageSize;

	let lastResponse: AxiosResponse<
		BenchmarkGoalListResponseOk | BenchmarkGoalListResponseClientError | string
	> | undefined;

	try {
		while (page !== null) {
			const queryString = buildQueryString({
				...sharedFilter,
				page: page ?? undefined,
				pageSize,
			});
			const url = queryString.length > 0 ? `/listBenchmarkGoals?${queryString}` : '/listBenchmarkGoals';
			const response = await http.get<
				BenchmarkGoalListResponseOk | BenchmarkGoalListResponseClientError | string
			>(url);
			lastResponse = response;

			if (response.status >= 200 && response.status < 300) {
				const data = response.data;

				if (typeof data === 'object' && data !== null && 'items' in data) {
					const okData = data as BenchmarkGoalListResponseOk;
					goals.push(...(okData.items ?? []));
					page = okData.nextPage ?? null;
					continue;
				}

				return {
					ok: true,
					statusCode: response.status,
					message: typeof data === 'string'
						? data
						: 'Benchmark goals retrieved (unknown format)',
					goals: null,
				};
			}

			const errorData = response.data as BenchmarkGoalListResponseClientError;
			return {
				ok: false,
				statusCode: errorData.status ?? response.status,
				message:
					errorData.title ??
					response.statusText ??
					'Error retrieving benchmark goals',
				goals: null,
				error: errorData,
			};
		}

		return {
			ok: true,
			statusCode: lastResponse?.status ?? 200,
			message: 'Benchmark goals retrieved successfully',
			goals,
		};
	} catch (error: unknown) {
		if (axios.isAxiosError(error)) {
			const statusCode = error.response?.status ?? 500;
			const message =
				error.response?.data?.message ||
				error.message ||
				'An error occurred while retrieving benchmark goals.';

			return {
				ok: false,
				statusCode,
				message,
				goals: null,
				error: error.response?.data ?? error.message,
			};
		}

		return {
			ok: false,
			statusCode: 500,
			message:
				(error as Error)?.message ??
				'Unknown error while retrieving benchmark goals.',
			goals: null,
			error: `${error}`,
		};
	}
};

export const createBenchmarkGoal = async (request: CreateBenchmarkGoalRequest) =>
	http.post<BenchmarkGoalResult>('/createBenchmarkGoal', request);

export const updateBenchmarkGoal = async (request: UpdateBenchmarkGoalRequest) =>
	http.post<BenchmarkGoalResult>('/updateBenchmarkGoal', request);

export const deleteBenchmarkGoal = async (request: DeleteBenchmarkGoalRequest) =>
	http.post<BenchmarkGoalResult>('/deleteBenchmarkGoal', request);
